// 🔧 outreach_queue.js
// ✅ Purpose: Admin-facing Outreach Queue for Let's Go Vote Sales Automation
// ✅ Features:
// - Shows prospects with outreach drafts ready for human review
// - Displays recommended channel, subject, and draft body
// - Allows admins to mark manually-sent outreach as sent
// - Allows admins to skip outreach without deleting the prospect
// - Schedules a 7-day follow-up when outreach is marked sent
// - Does NOT automatically send emails or social messages
// - Keeps human approval before external communication
// - Uses top-of-page success/error/info notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

function OutreachQueue() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [workingId, setWorkingId] = useState(null);

  const showStatus = (type, message) => {
    setStatus({ type, message });

    setTimeout(() => {
      setStatus(null);
    }, 20000);
  };

  const loadProspects = async () => {
    try {
      setLoading(true);

      console.log("📬 Loading Outreach Queue prospects...");

      const prospectsRef = collection(db, "prospects");
      const prospectsQuery = query(prospectsRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(prospectsQuery);

      const loadedProspects = snapshot.docs.map((prospectDoc) => ({
        id: prospectDoc.id,
        ...prospectDoc.data(),
      }));

      setProspects(loadedProspects);
    } catch (error) {
      console.error("❌ Failed to load Outreach Queue:", error);
      showStatus("error", "Failed to load Outreach Queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
  }, []);

  const sortByPriority = (a, b) => {
    const priorityA = Number(a.queuePriority || a.fitScore || 0);
    const priorityB = Number(b.queuePriority || b.fitScore || 0);

    if (priorityB !== priorityA) return priorityB - priorityA;

    const createdA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const createdB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;

    return createdA - createdB;
  };

  const outreachQueue = useMemo(() => {
    return prospects
      .filter((prospect) => prospect.outreachQueueStatus === "ready_for_review")
      .sort(sortByPriority);
  }, [prospects]);

  const handleMarkSent = async (prospect) => {
    const today = new Date();
    const followUp = new Date();

    followUp.setDate(today.getDate() + 7);

    const formatDate = (date) => date.toISOString().slice(0, 10);

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        status: "contacted",
        outreachStatus: "sent",
        outreachQueueStatus: "sent",
        collectionStage: "contacted",

        lastOutreachChannel: prospect.recommendedOutreachChannel || "email",
        lastOutreachDate: formatDate(today),
        outreachSentAt: serverTimestamp(),

        lastContacted: formatDate(today),
        followUpDate: formatDate(followUp),

        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus(
        "success",
        `${prospect.eventName} marked sent. Follow-up scheduled for ${formatDate(followUp)}.`
      );
    } catch (error) {
      console.error("❌ Failed to mark outreach sent:", error);
      showStatus("error", "Failed to mark outreach sent.");
    } finally {
      setWorkingId(null);
    }
  };

  const handleSkip = async (prospect) => {
    const confirmed = window.confirm(
      `Skip outreach for "${prospect.eventName}"? This will remove it from the active Outreach Queue.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        outreachQueueStatus: "skipped",
        collectionStage: "skipped",
        queueReason: "Skipped from Outreach Queue.",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus("success", `${prospect.eventName} skipped.`);
    } catch (error) {
      console.error("❌ Failed to skip outreach:", error);
      showStatus("error", "Failed to skip outreach.");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <SalesPageLayout
      title="Outreach Queue"
      subtitle="Review prepared outreach drafts, send manually, then mark them sent."
      status={status}
      actions={
        <>
          <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
            Sales Dashboard
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/follow-up-queue">
            Follow-up Queue
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/prospects">
            Prospects CRM
          </Link>
        </>
      }
    >
      {loading ? (
        <div className="alert alert-info">Loading Outreach Queue...</div>
      ) : outreachQueue.length === 0 ? (
        <div className="alert alert-info">
          No outreach drafts are ready for review.
        </div>
      ) : (
        <div className="row">
          {outreachQueue.map((prospect) => (
            <div className="col-12 mb-3" key={prospect.id}>
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{prospect.eventName}</strong>

                    {prospect.organizationName && (
                      <div className="small text-muted">
                        {prospect.organizationName}
                      </div>
                    )}
                  </div>

                  <span className="badge bg-info text-dark">
                    {prospect.recommendedOutreachChannel || "email"}
                  </span>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-4 mb-2">
                      <strong>Score:</strong> {prospect.fitScore || 0}/10
                    </div>

                    <div className="col-md-4 mb-2">
                      <strong>Research:</strong>{" "}
                      {prospect.researchConfidence
                        ? `${prospect.researchConfidence}%`
                        : "Not listed"}
                    </div>

                    <div className="col-md-4 mb-2">
                      <strong>Status:</strong> {prospect.status || "new"}
                    </div>
                  </div>

                  {prospect.email && (
                    <div className="mb-2">
                      <strong>Email:</strong>{" "}
                      <a href={`mailto:${prospect.email}`}>{prospect.email}</a>
                    </div>
                  )}

                  {prospect.facebookUrl && (
                    <div className="mb-2">
                      <strong>Facebook:</strong>{" "}
                      <a
                        href={prospect.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {prospect.facebookUrl}
                      </a>
                    </div>
                  )}

                  {prospect.instagramUrl && (
                    <div className="mb-2">
                      <strong>Instagram:</strong>{" "}
                      <a
                        href={prospect.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {prospect.instagramUrl}
                      </a>
                    </div>
                  )}

                  {prospect.outreachSubject && (
                    <div className="mb-2">
                      <strong>Subject:</strong> {prospect.outreachSubject}
                    </div>
                  )}

                  <div className="border rounded bg-light p-3 mb-3">
                    <strong>Draft:</strong>

                    <div style={{ whiteSpace: "pre-wrap" }} className="mt-2">
                      {prospect.emailOutreachDraft ||
                        prospect.outreachDraft ||
                        "No draft body found."}
                    </div>
                  </div>

                  {prospect.queueReason && (
                    <div className="small text-muted mb-3">
                      <strong>Queue Reason:</strong> {prospect.queueReason}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-success me-2"
                    onClick={() => handleMarkSent(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    {workingId === prospect.id ? "Saving..." : "Mark Sent"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={() => handleSkip(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    Skip
                  </button>

                  <Link className="btn btn-outline-primary" to="/admin/prospects">
                    Edit in CRM
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SalesPageLayout>
  );
}

export default OutreachQueue;